// The week's and the weekend's plan (/plan, opened by the Sunday and Friday
// pushes, Settings week_push / weekend_push): look back, what is on each day,
// loose ends, and a box to empty your head into. Pure; the view draws it.
// Show less: names, never counts of what is undone.
import type { TaskResource } from '@/kinds/task/apply'
import { isTemplate } from '@/kinds/task/schema'
import type { DateSpec } from '@/kinds/date/schema'
import { fixedOn, occurrencesBetween, type Fixed } from './dates'
import { isStale } from './jobs'
import { addDays, nextWeekOf, parseDate, weekendOf } from './time'

export type PlanKind = 'week' | 'weekend'

export interface PlanWindow {
  kind: PlanKind
  from: string
  to: string
  /** every day from..to */
  days: string[]
}

/** What today plans by default: the weekend from Friday to Sunday, else the week. */
export const planKindFor = (today: string): PlanKind => {
  const d = parseDate(today).getDay()
  return d === 5 || d === 6 || d === 0 ? 'weekend' : 'week'
}

/**
 * The days a plan covers. The week runs to Sunday: from today on a weekday,
 * from the coming Monday at the weekend (Sunday evening plans the week
 * ahead). The weekend is Saturday and Sunday: the coming one, or what is left
 * of this one.
 */
export const planWindow = (kind: PlanKind, today: string): PlanWindow => {
  const dow = parseDate(today).getDay()
  let from: string
  let to: string
  if (kind === 'week') {
    from = dow === 0 || dow === 6 ? nextWeekOf(today) : today
    to = addDays(from, (7 - parseDate(from).getDay()) % 7)
  } else {
    from = weekendOf(today)
    to = dow === 0 ? today : addDays(from, 1)
  }
  const days: string[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) days.push(d)
  return { kind, from, to, days }
}

const open = (t: TaskResource) =>
  !t.deleted_at &&
  !isTemplate(t.spec) &&
  t.status.state !== 'done' &&
  t.status.state !== 'dropped' &&
  t.status.state !== 'someday'

export interface PlanDay<D> {
  day: string
  /** appointments (dates with a time), earliest first */
  fixed: Fixed<D>[]
  /** all-day dates falling on the day (birthdays, bills) */
  dates: D[]
  /** open tasks scheduled for the day */
  tasks: TaskResource[]
  /** open tasks due that day and not scheduled inside the window */
  due: TaskResource[]
}

/** Each day of the window and what is on it. */
export const planDays = <D extends { spec: DateSpec; deleted_at?: string | null }>(
  w: PlanWindow,
  tasks: TaskResource[],
  dates: D[],
): PlanDay<D>[] => {
  const live = tasks.filter(open)
  const inWindow = (d?: string) => !!d && d >= w.from && d <= w.to
  return w.days.map((day) => ({
    day,
    fixed: fixedOn(dates, day),
    dates: dates.filter(
      (d) => !d.deleted_at && !d.spec.time && occurrencesBetween(d.spec, day, day).length > 0,
    ),
    tasks: live.filter((t) => t.spec.scheduled === day),
    due: live.filter((t) => t.spec.due === day && !inWindow(t.spec.scheduled)),
  }))
}

/** Whether nothing at all is on a day. */
export const isFree = <D>(d: PlanDay<D>): boolean =>
  !d.fixed.length && !d.dates.length && !d.tasks.length && !d.due.length

/**
 * Loose ends: open tasks whose day went by, or whose deadline went by with no
 * day ahead of it, oldest first. Giving one a day takes it off. The view
 * shows a few, by name.
 */
export const looseEnds = (w: PlanWindow, tasks: TaskResource[], today: string): TaskResource[] => {
  const before = w.from > today ? today : w.from
  const day = (t: TaskResource) => {
    const sc = t.spec.scheduled
    if (sc && sc >= today) return ''
    const s = sc && sc < before ? sc : ''
    const d = t.spec.due && t.spec.due < before ? t.spec.due : ''
    return [s, d].filter(Boolean).sort()[0] ?? ''
  }
  return tasks
    .filter((t) => open(t) && !!day(t))
    .sort((a, b) => (day(a) < day(b) ? -1 : day(a) > day(b) ? 1 : a.id < b.id ? -1 : 1))
}

/** What got done in the seven days before now, newest first (completions may be named). */
export const lookBack = (tasks: TaskResource[], now = new Date()): TaskResource[] => {
  const cutoff = new Date(now.getTime() - 7 * 86_400_000).toISOString()
  return tasks
    .filter(
      (t) =>
        !t.deleted_at &&
        t.status.state === 'done' &&
        !!t.status.completed_at &&
        t.status.completed_at >= cutoff,
    )
    .sort((a, b) => (a.status.completed_at! < b.status.completed_at! ? 1 : -1))
}

/** House jobs that will be due by the end of the window, for the weekend. */
export const jobsFor = (w: PlanWindow, tasks: TaskResource[]): TaskResource[] =>
  tasks.filter((t) => !t.deleted_at && isStale(t, w.to))

/** The words handed to Prime when asking it to help shape the plan. */
export const primeAsk = (w: PlanWindow, range: string, lines: string[]): string => {
  const what = w.kind === 'week' ? `my week (${range})` : `my weekend (${range})`
  const dump = lines.length ? `\n\nOn my mind:\n${lines.map((l) => `- ${l}`).join('\n')}` : ''
  return `Help me plan ${what}: look at what's on and what slipped, and suggest which day each thing goes on.${dump}`
}
